import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MunilyFilterFieldSize } from '../filter-fields/filter-field.types';
import { MunilyTextFieldErrorMessages, MunilyTextFieldWidthLayout } from '../text-field/text-field.types';
import {
  MUNILY_FILTER_TEXT_FIELD_DEFAULTS,
  MunilyFilterTextFieldInputType,
} from './filter-text-field.types';

@Component({
  selector: 'app-filter-text-field',
  templateUrl: './filter-text-field.component.html',
  styleUrls: ['./filter-text-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterTextFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() size: MunilyFilterFieldSize = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.size;
  @Input() widthLayout: MunilyTextFieldWidthLayout = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.widthLayout;
  @Input() maxWidth?: string;
  @Input() type: MunilyFilterTextFieldInputType = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.type;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  /** Controla si se reserva espacio inferior para hints/counter. */
  @Input() reserveHintSpace = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.reserveHintSpace;
  @Input() iconLeading?: string;
  @Input() iconTrailing?: string;
  @Input() prefixText?: string;
  @Input() hasCharacterCounter = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.hasCharacterCounter;
  @Input() maxLength?: number;
  @Input() floatLabel: FloatLabelType = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.floatLabel;
  @Input() disabled = false;
  @Input() successful = false;
  @Input() autocomplete = MUNILY_FILTER_TEXT_FIELD_DEFAULTS.autocomplete;

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextFieldErrorMessages = {};

  @Output() valueChange = new EventEmitter<string>();

  public formFieldClasses: string[] = [];

  @HostBinding('class.munily-ff-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl('');
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get currentLength(): number {
    const v = this.resolvedControl.value;
    return v != null ? String(v).length : 0;
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.syncDisabled();
    this.wireControlUpdates();
    this.syncFormFieldClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control']) {
      this.destroy$.next();
      this.syncDisabled();
      this.wireControlUpdates();
    }
    if (
      changes['size'] ||
      changes['successful'] ||
      changes['disabled'] ||
      changes['control'] ||
      changes['reserveHintSpace'] ||
      changes['hint'] ||
      changes['hasCharacterCounter'] ||
      changes['maxLength']
    ) {
      this.syncFormFieldClasses();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onInput(): void {
    this.valueChange.emit(this.resolvedControl.value as string);
  }

  onBlur(): void {
    this.resolvedControl.markAsTouched();
    this.syncFormFieldClasses();
  }

  private syncFormFieldClasses(): void {
    const next: string[] = [];

    if (this.size === 'large') next.push('munily-filter-text-field--size-large');
    if (this.size === 'small') next.push('munily-filter-text-field--size-small');

    if (this.showSuccessOutline()) next.push('munily-filter-text-field--success');
    if (this.showError()) next.push('mat-form-field-invalid');
    if (!this.reserveHintSpace) next.push('munily-filter-text-field--no-hint-space');

    this.formFieldClasses = next;
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncFormFieldClasses();
      this.cdr.markForCheck();
    });
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncFormFieldClasses();
      this.cdr.markForCheck();
    });
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (key === 'email') return 'Email no válido';
    if (key === 'minlength' && err?.requiredLength != null) return `Mínimo ${err.requiredLength} caracteres`;
    if (key === 'maxlength' && err?.requiredLength != null) return `Máximo ${err.requiredLength} caracteres`;
    if (key === 'min' || key === 'max') return 'Valor fuera de rango';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
